import { ContextWindow } from "@asup/context-menu";
import {
  AnalysisMethodControl,
  AnalysisSetControl,
  ARS,
  AsyncButton,
  Control211,
  createNewAnalysisSet,
  createNewDataSubset,
  createNewGroupingFactor,
  DataSubsetControl,
  GroupingFactorControl,
  PATCH_ARS_STATE,
  useARSContext,
} from "aic";
import { HierarchyContext } from "contexts/HierarchyContext";
import { APPLY_ARS_UPDATES, DISCARD_ARS_UPDATES } from "contexts/tlf/interface";
import { TlfContext } from "contexts/TlfContext";
import { eAccessRights } from "functions/api/enums";
import { ReactNode, useContext, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { AreYouSureModal } from "../common/modals/AreYouSureModal";
import { ArsSetType } from "./interface";

/**
 * Props for ArsSetWindow.
 */
export interface ArsSetWindowProps {
  /** Base id string for DOM elements. */
  itemId: string;
  /** Callback to close the popup. */
  onClose: (newItemId?: string) => void;
  /** Which ARS set type this window controls. */
  type: ArsSetType;
  /** Whether the popup is visible. */
  visible: boolean;
}

/**
 * Shared selection/editing window for analysis set, data subset, and grouping factor.
 *
 * @param props - Component props.
 * @param props.itemId - Base id used for element identifiers.
 * @param props.onClose - Callback to close the popup window.
 * @param props.type - Which ARS set type is controlled by the window.
 * @param props.visible - Whether the popup should be visible.
 * @returns The contextual set-selection window.
 */
export const ArsSetWindow = ({ type, itemId, onClose, visible }: ArsSetWindowProps): ReactNode => {
  const hierarchyContext = useContext(HierarchyContext);
  const canEdit =
    hierarchyContext?.accessRights.includes(eAccessRights.WriteToTlfItemsInHierarchy) ?? false;
  const tlfContext = useContext(TlfContext);
  const { state, dispatch } = useARSContext();

  const [selectedId, setSelectedId] = useState(itemId);
  const [showAreYouSure, setShowAreYouSure] = useState(false);
  // This makes sure all changes trigger a rerender
  const hasUpdates = tlfContext?.delayedHierarchyUpdates || state.changed;

  useEffect(() => {
    setSelectedId(itemId);
  }, [itemId]);

  const selectedEntity = (() => {
    switch (type) {
      case "analysisMethod":
        return state.analysisMethods[selectedId];
      case "analysisSet":
        return state.analysisSets[selectedId];
      case "dataSubset":
        return state.dataSubsets[selectedId];
      case "groupingFactor":
        return state.groupingFactors[selectedId];
    }
  })();

  /**
   * Creates a new entity for the active type in hierarchy context.
   */
  const handleCreateNew = () => {
    let newId: string;

    switch (type) {
      /* istanbul ignore next -- analysis methods are intentionally not created from this window */
      case "analysisMethod": {
        // Analysis methods are not created in this window, so this case should be unreachable
        break;
      }

      case "analysisSet": {
        const newLength = Object.values(state.analysisSets).length + 1;
        const newSet: ARS.AnalysisSet = createNewAnalysisSet(`AnalysisSet_${newLength}`);
        newId = newSet.id;
        dispatch({
          operation: PATCH_ARS_STATE,
          updates: [{ dataPath: `/analysisSets/${newId}`, value: newSet }],
        });
        setSelectedId(newId);
        break;
      }

      case "dataSubset": {
        const newLength = Object.values(state.dataSubsets).length + 1;
        const newSubset: ARS.DataSubset = createNewDataSubset(`DataSubset_${newLength}`);
        newId = newSubset.id;
        dispatch({
          operation: PATCH_ARS_STATE,
          updates: [{ dataPath: `/dataSubsets/${newId}`, value: newSubset }],
        });
        setSelectedId(newId);
        break;
      }

      case "groupingFactor": {
        const newLength = Object.values(state.groupingFactors).length + 1;
        const newGroupingFactor: ARS.GroupingFactor = {
          ...createNewGroupingFactor(`GroupingFactor_${newLength}`),
        };
        newId = newGroupingFactor.id;
        dispatch({
          operation: PATCH_ARS_STATE,
          updates: [{ dataPath: `/groupingFactors/${newId}`, value: newGroupingFactor }],
        });
        setSelectedId(newId);
        break;
      }
    }
  };

  /**
   * Creates a copy of the selected entity for the active type in hierarchy context.
   */
  const handleCopyAndEdit = () => {
    /* istanbul ignore else -- copy actions are only reachable when a selectable entity exists with edit access */
    if (selectedEntity) {
      const copyId = crypto.randomUUID();

      switch (type) {
        /* istanbul ignore next -- analysis methods are intentionally not copied from this window */
        case "analysisMethod": {
          // Analysis methods are not copied in this window, so this case should be unreachable
          break;
        }

        case "analysisSet": {
          const copy: ARS.AnalysisSet = {
            ...(selectedEntity as ARS.AnalysisSet),
            id: copyId,
            name: `${selectedEntity.name} (copy)`,
            order: Object.values(state.analysisSets).length + 1,
          };
          dispatch({
            operation: PATCH_ARS_STATE,
            updates: [{ dataPath: `/analysisSets/${copyId}`, value: copy }],
          });
          setSelectedId(copyId);
          break;
        }

        case "dataSubset": {
          const copy: ARS.DataSubset = {
            ...(selectedEntity as ARS.DataSubset),
            id: copyId,
            name: `${selectedEntity.name} (copy)`,
            order: Object.values(state.dataSubsets).length + 1,
          };
          dispatch({
            operation: PATCH_ARS_STATE,
            updates: [{ dataPath: `/dataSubsets/${copyId}`, value: copy }],
          });
          setSelectedId(copyId);
          break;
        }

        case "groupingFactor": {
          const copy: ARS.GroupingFactor = {
            ...(selectedEntity as ARS.GroupingFactor),
            id: copyId,
            name: `${selectedEntity.name} (copy)`,
          };
          dispatch({
            operation: PATCH_ARS_STATE,
            updates: [{ dataPath: `/groupingFactors/${copyId}`, value: copy }],
          });
          setSelectedId(copyId);
          break;
        }
      }
    }
  };

  /**
   * Saves active selection changes to TLF context and closes.
   */
  const handleSaveAndClose = () => {
    tlfContext?.dispatch({
      operation: APPLY_ARS_UPDATES,
      calledFrom: "ArsSetWindow handleSaveAndClose",
    });
    onClose(selectedId !== itemId ? selectedId : undefined);
  };

  /**
   * Closes the window or shows discard confirmation if there are pending changes.
   */
  const handleClose = () => {
    if (hasUpdates) {
      setShowAreYouSure(true);
    } else {
      setSelectedId(itemId);
      onClose();
    }
  };

  /**
   * Discards pending entities and closes the window.
   */
  const handleConfirmDiscard = () => {
    /* istanbul ignore else -- discard confirmation is only shown when ARS changes exist */
    if (hasUpdates) {
      tlfContext?.dispatch({
        operation: DISCARD_ARS_UPDATES,
        calledFrom: "ArsSetWindow handleConfirmDiscard",
      });
    }
    setSelectedId(itemId);
    setShowAreYouSure(false);
    onClose();
  };

  useEffect(() => {
    /**
     * Handles Escape-key presses while there are pending changes.
     *
     * @param event - Keyboard event used to detect Escape.
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      /* istanbul ignore else - Unreachable when event.key is not Escape or when visible/hasPendingChanges is false */
      if (event.key === "Escape" && visible && hasUpdates) {
        event.preventDefault();
        setShowAreYouSure(true);
      }
    };

    if (visible && hasUpdates) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [hasUpdates, visible]);

  const title = type
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
  const modalIdPrefix = type.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();

  const options = (() => {
    switch (type) {
      case "analysisMethod":
        return [
          ...(selectedId === "" ? [{ value: "", label: "--- Select an analysis method ---" }] : []),
          ...Object.values(state.analysisMethods).map((method) => ({
            value: method.id,
            label: method.name,
          })),
        ];
      case "analysisSet":
        return [
          ...(selectedId === "" ? [{ value: "", label: "--- Select an analysis set ---" }] : []),
          ...Object.values(state.analysisSets).map((set) => ({ value: set.id, label: set.name })),
        ];
      case "dataSubset":
        return [
          { value: "", label: "--- No data subset ---" },
          ...Object.values(state.dataSubsets).map((subset) => ({
            value: subset.id,
            label: subset.name,
          })),
        ];
      case "groupingFactor":
        return [
          ...(selectedId === "" ? [{ value: "", label: "--- Select a grouping factor ---" }] : []),
          ...Object.values(state.groupingFactors).map((factor) => ({
            value: factor.id,
            label: factor.name,
          })),
        ];
    }
  })();

  return !hierarchyContext || !tlfContext ? null : (
    <>
      <ContextWindow
        id={`${modalIdPrefix}-picker-modal`}
        title={title}
        visible={visible}
        style={{ minWidth: "800px" }}
        onClose={handleClose}
      >
        <Container>
          <Control211
            id={`${modalIdPrefix}-select`}
            label={title}
            disabled={!canEdit}
            options={options}
            value={selectedId}
            setValue={setSelectedId}
          />

          {type === "analysisMethod" && selectedEntity && (
            <AnalysisMethodControl
              dataPath={`/analysisMethods/${selectedId}`}
              canRename={false}
              disabled={true}
            />
          )}

          {type === "analysisSet" && selectedEntity && (
            <AnalysisSetControl
              dataPath={`/analysisSets/${selectedId}`}
              canRename={canEdit}
              disabled={!canEdit}
            />
          )}

          {type === "dataSubset" && selectedId && (
            <DataSubsetControl
              dataPath={`/dataSubsets/${selectedId}`}
              canRename={canEdit}
              disabled={!canEdit}
            />
          )}

          {type === "groupingFactor" && (
            <GroupingFactorControl
              dataPath={`/groupingFactors/${selectedId}`}
              canRename={canEdit}
              disabled={!canEdit}
            />
          )}

          <div
            className="d-flex justify-content-end gap-2 mb-3"
            style={{ bottom: 0 }}
          >
            {canEdit && (
              <>
                <AsyncButton
                  id={`create-new-${modalIdPrefix}-button`}
                  variant="outline-secondary"
                  onClick={handleCreateNew}
                  aria-label="Create new"
                  disabled={type === "analysisMethod"}
                >
                  New
                </AsyncButton>
                <AsyncButton
                  id={`edit-existing-${modalIdPrefix}-button`}
                  variant="primary"
                  onClick={handleCopyAndEdit}
                  aria-label={`Create a copy of the ${title.toLowerCase()} for editing`}
                  disabled={!selectedId || type === "analysisMethod"}
                >
                  Copy
                </AsyncButton>
                <AsyncButton
                  id={`save-${modalIdPrefix}-button`}
                  variant="primary"
                  onClick={handleSaveAndClose}
                  disabled={!hasUpdates && selectedId === itemId}
                  aria-label="Save"
                >
                  Save
                </AsyncButton>
              </>
            )}
            <AsyncButton
              id={`close-${modalIdPrefix}-picker-button`}
              variant="secondary"
              onClick={handleClose}
            >
              Close
            </AsyncButton>
          </div>
        </Container>
      </ContextWindow>

      <AreYouSureModal
        id={`discard-${modalIdPrefix}-changes`}
        show={showAreYouSure}
        title="Discard changes?"
        message={`You have unsaved changes, do you want to discard them?`}
        onOk={handleConfirmDiscard}
        onClose={() => setShowAreYouSure(false)}
      />
    </>
  );
};

ArsSetWindow.displayName = "ArsSetWindow";
